import Decimal from 'decimal.js';
import type { APInvoiceLineItemSplit } from '@erp/rpc';

export interface SplitGroupLine {
  id: string;
  lineNumber: number;
  quantity?: string | number | null;
  totalPrice?: string | null;
  split?: APInvoiceLineItemSplit | null;
}

/** The parts of one split invoice line, in line-number order. */
export interface SplitGroupInfo {
  sourceLineItemId: string;
  originalLineNumber: number | null;
  originalTotalPrice: string;
  memberIds: string[];
  summedQuantity: string;
  summedTotal: string;
}

export interface SplitGroups {
  groupIdByLineId: Map<string, string>;
  groups: Map<string, SplitGroupInfo>;
}

export interface SplitMemberPosition {
  index: number;
  count: number;
  isLast: boolean;
}

function toDecimal(value: string | number | null | undefined): Decimal {
  try {
    return new Decimal(value ?? 0);
  } catch {
    return new Decimal(0);
  }
}

/**
 * Group the parts of each split line by their shared source line. A group
 * with a single part left is not a split any more and is dropped.
 */
export function buildSplitGroups(
  lines: readonly SplitGroupLine[],
): SplitGroups {
  const membersBySource = new Map<
    string,
    { root: APInvoiceLineItemSplit; members: SplitGroupLine[] }
  >();
  for (const line of lines) {
    if (!line.split) continue;
    const entry = membersBySource.get(line.split.sourceLineItemId) ?? {
      root: line.split,
      members: [],
    };
    entry.members.push(line);
    membersBySource.set(line.split.sourceLineItemId, entry);
  }

  const groups = new Map<string, SplitGroupInfo>();
  const groupIdByLineId = new Map<string, string>();
  for (const [sourceId, { root, members }] of membersBySource) {
    if (members.length < 2) continue;
    const ordered = [...members].sort((a, b) => a.lineNumber - b.lineNumber);
    groups.set(sourceId, {
      sourceLineItemId: sourceId,
      originalLineNumber: root.originalLineNumber,
      originalTotalPrice: root.originalTotalPrice,
      memberIds: ordered.map((line) => line.id),
      summedQuantity: ordered
        .reduce(
          (sum, line) => sum.plus(toDecimal(line.quantity)),
          new Decimal(0),
        )
        .toString(),
      summedTotal: ordered
        .reduce(
          (sum, line) => sum.plus(toDecimal(line.totalPrice)),
          new Decimal(0),
        )
        .toFixed(2),
    });
    for (const line of ordered) groupIdByLineId.set(line.id, sourceId);
  }
  return { groupIdByLineId, groups };
}

export function getSplitGroup(
  splitGroups: SplitGroups,
  lineItemId: string,
): SplitGroupInfo | null {
  const groupId = splitGroups.groupIdByLineId.get(lineItemId);
  return groupId ? (splitGroups.groups.get(groupId) ?? null) : null;
}

export function getSplitMemberPosition(
  splitGroups: SplitGroups,
  lineItemId: string,
): SplitMemberPosition | null {
  const group = getSplitGroup(splitGroups, lineItemId);
  if (!group) return null;
  const index = group.memberIds.indexOf(lineItemId);
  return {
    index: index + 1,
    count: group.memberIds.length,
    isLast: index === group.memberIds.length - 1,
  };
}

/** True when the parts still add up to the original invoice line. */
export function splitGroupBalances(group: SplitGroupInfo): boolean {
  return toDecimal(group.summedTotal).equals(
    toDecimal(group.originalTotalPrice),
  );
}

const SPLIT_MEMBER_ROW_CLASS = 'border-l-4 border-l-violet-400 bg-violet-50/30';

export function splitMemberRowClass(
  splitGroups: SplitGroups,
  lineItemId: string,
): string {
  return getSplitGroup(splitGroups, lineItemId) ? SPLIT_MEMBER_ROW_CLASS : '';
}
